import React, {
  useMemo,
  FC,
  useContext,
  useState,
  useEffect,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import ToolTip from 'core/components/Tooltip';
import PinIcon from 'core/icons/pin';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import SuggestedCodesList from 'views/EMR/components/ServiceLookup/SuggestedCodesList';
import {
  useFormContext,
} from 'react-hook-form';
import {
  isDev,
} from 'env';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  serializeContent,
} from 'views/EMR/Settings/helper';
import isNil from 'lodash/isNil';
import SaltIconButton from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltIconButton';
import {
  SaltType,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltMenu';

const actions = ['ai', 'salt', 'pin'] as const;
export type ServiceToolbarAction = typeof actions[number];

function SALTServiceItem() {
  const {
    fetchCodeFromVisitNote,
    patientId,
    visitNoteId: currentVisitNoteId,
    isSaltLoading: isSaltInProgress,
    type,
  } = useContext(ServiceContext);

  const saltType: SaltType = useMemo(
    () => {
      switch (type) {
        case 'ICDCode':
          return 'assessmentCodes';
        case 'CPTCode':
          return 'billingCodes';
        case 'CustomCode':
          return 'customServiceCodes';
        default:
          return 'unknown';
      }
    },
    [type],
  );

  const [selectedPastVisitNoteId, setSelectedPastVisitNoteId] = useState<number | null>(null);

  const isPastVisitNoteSelected = useMemo(
    () => !isNil(selectedPastVisitNoteId),
    [selectedPastVisitNoteId],
  );

  const handleVisitNoteSectionSalt = async () => {
    await fetchCodeFromVisitNote?.(patientId ?? null, selectedPastVisitNoteId);

    if (!isNil(selectedPastVisitNoteId)) {
      setSelectedPastVisitNoteId(null);
    }
  };

  useEffect(() => {
    if (isPastVisitNoteSelected) {
      handleVisitNoteSectionSalt();
    }
  }, [selectedPastVisitNoteId]);

  return (
    <SaltIconButton
      patientId={patientId ?? 0}
      visitNoteId={currentVisitNoteId ?? 0}
      isSaltInProgress={isSaltInProgress ?? false}
      saltType={saltType}
      handleSalt={setSelectedPastVisitNoteId}
    />
  );
}

function AIServiceItem() {
  const {
    saveVisitNote,
    type,
    onChange,
  } = useContext(ServiceContext);
  const { watch } = useFormContext();
  const additionalAssessment = watch('additionalAssessment');
  const isAssessmentCode = type === 'ICDCode';
  const disabled = useMemo(() => {
    const serialized = serializeContent(
      additionalAssessment,
    );

    return isAssessmentCode && isEmptyString(serialized);
  }, [isAssessmentCode, additionalAssessment]);

  return (
    <ToolTip
      title="ICD-10 suggestions are based on the Assessment. Please add an Assessment to continue."
      hide={!disabled}
    >
      <span>
        <SuggestedCodesList
          saveVisitNote={saveVisitNote}
          type={type ?? 'CustomCode'}
          onChange={onChange}
          disabled={disabled}
        />
      </span>
    </ToolTip>
  );
}

function PINServiceItem() {
  const {
    favorites = [],
    onToggleFavoritesList,
    showFavorites,
  } = useContext(ServiceContext);

  const favoritesPinTooltipTitle = useMemo(() => (
    favorites?.length > 0 ? 'Show pinned services' : 'You have no pinned services'
  ), [favorites]);

  return (
    <ToolTip title={favoritesPinTooltipTitle}>
      <span>
        <IconButton
          size="small"
          tabIndex={-1}
          className="is-favorite disabled:opacity-50 nw-pin-button"
          disabled={favorites?.length === 0}
          onClick={onToggleFavoritesList}
          onMouseDown={(event) => {
            // prevent blur event to propagate to the lookup to prevent it from closing
            event.preventDefault();
          }}
        >
          <PinIcon className={clsx('!text-base text-gray-500', {
            '!text-blue-500': showFavorites,
          })}
          />
        </IconButton>
      </span>
    </ToolTip>
  );
}

const actionComponents: Record<ServiceToolbarAction, FC> = {
  salt: SALTServiceItem,
  ai: AIServiceItem,
  pin: PINServiceItem,
};

export default function ServiceToolbar() {
  const { actions, formEnabled } = useContext(ServiceContext);

  return (
    <div className="flex items-center">
      {actions?.map((action) => {
        if (action === 'ai' && !formEnabled) {
          if (isDev) {
            console.warn(
              '[ServicesLookup] The "ai" toolbar action requires a React Hook Form context. '
              + 'Pass formEnabled={true} and ensure the component is wrapped in a FormProvider.',
            );
          }
          return null;
        }
        const Component = actionComponents[action];
        return <Component key={action} />;
      })}
    </div>
  );
}
